import { useSearchParams } from "next/navigation";

/**
 * Thin utility wrapper provider for useSearchParams that can check search params including bracketed array syntax (e.g. "queryStringKey or queryStringKey[]")
 * This is due to our usage of leaflyShopStringify and the qs package  when serializing some URLs.
 *
 * @returns {getSearchParamValues} utility function for getting all query string values for a given key, corrected for bracketed array syntax
 */
export const useGetSearchParamValues = () => {
  const searchParams = useSearchParams();

  /**
   * Utility function for getting all query string values for a given key, corrected for bracketed array syntax (e.g. "queryStringKey or queryStringKey[]")
   *
   * @param queryStringKey utility will check query string for the given key, as well as "queryStringKey[]"
   * @returns {string[]} all query string values for a given key, corrected for bracketed array syntax
   */
  const getSearchParamValues = (queryStringKey: string): string[] => {
    // * Due to our usage of leaflyShopStringify, search param values might appear as arrays with bracketed syntax.
    // * If/when we deprecate our usage of bracketed query string parameters on the frontend, the below logic can also be deprecated.
    if (searchParams) {
      if (searchParams.getAll(queryStringKey).length > 0) {
        return searchParams.getAll(queryStringKey);
      }
      const bracketedQSKey = `${queryStringKey}[]`;
      if (searchParams.getAll(bracketedQSKey).length > 0) {
        return searchParams.getAll(bracketedQSKey);
      }
    }

    return [];
  };

  return getSearchParamValues;
};

export default useGetSearchParamValues;
